import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { shareApkBackup } from '../utilities/api';
import '../masters/CategoryMaster.css';
import './Mobilebillingapp.css';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Share one app entry's install links by email. The recipient gets the same branded
 * HTML treatment as the Report-an-Issue email, with Reply-To pointed at whoever shared
 * it — so a question about the app comes back to a person, not an unmonitored inbox.
 */
const ShareApkDialog = ({ visible, apkData, onHide, toast }) => {
    const [email, setEmail] = useState('');
    const [senderEmail, setSenderEmail] = useState('');
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setEmail('');
        setSenderEmail('');
        setMessage('');
        setErrors({});
    }, [visible]);

    const validate = () => {
        const errs = {};
        const to = email.trim();
        if (!to) errs.email = 'Enter the email address to share with';
        else if (!EMAIL_PATTERN.test(to)) errs.email = 'Enter a valid email address';

        const from = senderEmail.trim();
        if (from && !EMAIL_PATTERN.test(from)) errs.senderEmail = 'Enter a valid email address';

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSend = async () => {
        if (!validate() || !apkData) return;
        setSending(true);
        try {
            const data = await shareApkBackup({
                apkid: apkData.apkid,
                email: email.trim(),
                shared_by: localStorage.getItem('username') || 'GSPOS Team',
                shared_by_email: senderEmail.trim(),
                message: message.trim()
            });
            if (data?.status === false) {
                toast?.current?.show({ severity: 'error', summary: 'Not Sent', detail: data?.message || 'Could not send the email.', life: 4000 });
                return;
            }
            toast?.current?.show({ severity: 'success', summary: 'App Link Shared', detail: data?.message || `App links sent to ${email.trim()}`, life: 3500 });
            onHide();
        } catch (err) {
            const detail = err?.response?.data?.message || err?.message || 'Could not send the email. Please try again.';
            toast?.current?.show({ severity: 'error', summary: 'Send Failed', detail, life: 4000 });
        } finally {
            setSending(false);
        }
    };

    const dialogHeader = (
        <div className="sub-dialog-header">
            <span className="sub-dialog-header-icon">
                <i className="pi pi-share-alt" />
            </span>
            <div>
                <div className="sub-dialog-header-text">Share App Link</div>
                <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>Email the install links to whoever requested them</div>
            </div>
        </div>
    );

    const dialogFooter = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" icon="pi pi-times" className="sub-dialog-cancel" onClick={onHide} disabled={sending} />
            <Button label={sending ? 'Sending…' : 'Send'} icon={sending ? 'pi pi-spin pi-spinner' : 'pi pi-send'} className="sub-dialog-save" onClick={handleSend} disabled={sending} />
        </div>
    );

    return (
        <Dialog
            header={dialogHeader}
            visible={visible}
            style={{ width: '560px' }}
            breakpoints={{ '620px': '94vw' }}
            modal
            position="center"
            footer={dialogFooter}
            onHide={onHide}
            draggable={false}
            resizable={false}
            className="sub-master-dialog mba-dialog p-fluid"
        >
            <div className="sub-form-grid">
                {/* What is being shared — so the wrong row's links never go out unnoticed. */}
                <div className="sub-field-group sub-field-full">
                    <div className="mba-share-target">
                        <i className="pi pi-mobile" style={{ color: '#7c3aed' }} />
                        <span>
                            <strong>{apkData?.client_name || apkData?.client_id || '—'}</strong>
                            {' · '}
                            {apkData?.branch_name || 'All Branches'}
                            {apkData?.app_version ? ` · v${apkData.app_version}` : ''}
                        </span>
                        <span className="mba-share-links">
                            {apkData?.googledrive_link && (
                                <span className="mba-share-chip">
                                    <i className="pi pi-cloud-download" /> APK
                                </span>
                            )}
                            {apkData?.play_storelink && (
                                <span className="mba-share-chip">
                                    <i className="pi pi-google" /> Play Store
                                </span>
                            )}
                        </span>
                    </div>
                </div>

                {/* Recipient */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-envelope sub-field-label-icon" />
                        Send To <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <InputText
                        value={email}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            if (errors.email)
                                setErrors((prev) => {
                                    const err = { ...prev };
                                    delete err.email;
                                    return err;
                                });
                        }}
                        onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                        placeholder="requester@example.com"
                        className={`w-full${errors.email ? ' p-invalid' : ''}`}
                    />
                    {errors.email && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.email}
                        </span>
                    )}
                </div>

                {/* Reply-to */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-reply sub-field-label-icon" />
                        Your Email <span className="mba-muted" style={{ textTransform: 'none', letterSpacing: 0 }}>(optional — replies come back here)</span>
                    </label>
                    <InputText
                        value={senderEmail}
                        onChange={(e) => {
                            setSenderEmail(e.target.value);
                            if (errors.senderEmail)
                                setErrors((prev) => {
                                    const err = { ...prev };
                                    delete err.senderEmail;
                                    return err;
                                });
                        }}
                        placeholder="you@gravitysoft.in"
                        className={`w-full${errors.senderEmail ? ' p-invalid' : ''}`}
                    />
                    {errors.senderEmail && (
                        <span className="sub-error-msg">
                            <i className="pi pi-exclamation-circle" /> {errors.senderEmail}
                        </span>
                    )}
                </div>

                {/* Optional note */}
                <div className="sub-field-group sub-field-full">
                    <label className="sub-field-label">
                        <i className="pi pi-comment sub-field-label-icon" />
                        Message <span className="mba-muted" style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                    </label>
                    <InputTextarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={3}
                        autoResize
                        placeholder="e.g. Install this build on the counter tablet before Monday."
                        className="w-full"
                    />
                </div>

                <div className="sub-field-group sub-field-full">
                    <span className="mba-dialog-summary">
                        <i className="pi pi-info-circle" style={{ marginTop: 2 }} />
                        <span>The recipient gets the download button for the APK and the Play Store link, in a GravitySoft-branded email.</span>
                    </span>
                </div>
            </div>
        </Dialog>
    );
};

export default ShareApkDialog;
